import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertCircle, Loader2, KeyRound, ArrowRight, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * iter188: página pública para RETOMAR un borrador público.
 *
 * - Ruta `/auxiliares-justicia/otros-distritos/retomar/:token` → carga por token UUID.
 * - Ruta `/auxiliares-justicia/otros-distritos/retomar` → pide código corto.
 *
 * En ambos casos, al encontrar el borrador:
 * - Guarda los datos y distrito_origen en los storages que lee el formulario.
 * - Redirige a `/otros-distritos/formulario`.
 */
export default function OtrosDistritosRetomarPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [cargandoToken, setCargandoToken] = useState(Boolean(token));
  const [errorToken, setErrorToken] = useState('');
  const [codigo, setCodigo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorCodigo, setErrorCodigo] = useState('');

  const hidratarYNavegar = (borr) => {
    try {
      sessionStorage.setItem('aux_otro_distrito_origen', borr.distrito_origen || '');
      localStorage.setItem(
        'aux_otro_distrito_formdata',
        JSON.stringify({
          formData: borr.datos || {},
          cursoEsteAnio: Boolean(borr.curso_este_anio),
        }),
      );
      localStorage.setItem(
        'aux_otro_distrito_borrador',
        JSON.stringify({
          token: borr.token,
          codigo: borr.codigo,
          email_contacto: borr.email_contacto,
          email_enviado: true,
          // iter191: señalar al formulario que arranque directo en paso 2
          // (si llegamos acá es porque el user ya completó paso 1).
          resumir_paso: 2,
        }),
      );
    } catch { /* no-op */ }
    navigate('/auxiliares-justicia/otros-distritos/formulario', { replace: true });
  };

  // Cargar por token UUID (si viene en la URL).
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/auxiliares-justicia/publico/borrador/${token}`,
        );
        hidratarYNavegar(res.data);
      } catch (err) {
        setErrorToken(err.response?.data?.detail || 'No encontramos el borrador.');
      } finally {
        setCargandoToken(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const retomarConCodigo = async (e) => {
    e?.preventDefault?.();
    setErrorCodigo('');
    if (!codigo.trim()) {
      setErrorCodigo('Pegá el código recibido por email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/auxiliares-justicia/publico/borrador/por-codigo`,
        { codigo: codigo.trim() },
      );
      hidratarYNavegar(res.data);
    } catch (err) {
      setErrorCodigo(err.response?.data?.detail || 'Código no válido.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Estado: cargando con token ───
  if (cargandoToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-amber-600 mx-auto mb-3" />
          <p className="text-sm text-gray-600">Recuperando tu borrador…</p>
        </div>
      </div>
    );
  }

  // ─── Estado: token inválido ───
  if (errorToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-red-200" data-testid="retomar-error-token">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold mb-2">Enlace no válido</h2>
            <p className="text-sm text-gray-600 mb-4">{errorToken}</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Link to="/auxiliares-justicia/otros-distritos/retomar">
                <Button variant="outline" data-testid="btn-ir-retomar-codigo">
                  <KeyRound className="w-4 h-4 mr-2" /> Probar con código
                </Button>
              </Link>
              <Link to="/auxiliares-justicia/otros-distritos">
                <Button variant="ghost">
                  <Home className="w-4 h-4 mr-2" /> Empezar de nuevo
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Modo: ingreso de código corto ───
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-6">
          <img src="/logo-colegio.png" alt="Logo del Colegio" className="h-20 w-auto object-contain" />
        </div>
        <Card data-testid="retomar-codigo-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="w-5 h-5 text-amber-600" />
              Retomar tu inscripción
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Si guardaste un borrador antes, podés retomarlo usando el link que te enviamos por
              email, o pegando acá el código de rescate (tipo <code className="bg-gray-100 px-1 rounded">AUX-7X3F9</code>).
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={retomarConCodigo} className="space-y-4">
              <div>
                <Label htmlFor="codigo-rescate" className="text-sm font-medium">
                  Código de rescate
                </Label>
                <Input
                  id="codigo-rescate"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                  placeholder="AUX-XXXXX"
                  className="font-mono tracking-wider mt-1"
                  data-testid="retomar-input-codigo"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lo encontrás en el email que te mandamos al guardar el borrador.
                </p>
              </div>

              {errorCodigo && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span data-testid="retomar-error-codigo">{errorCodigo}</span>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-amber-600 hover:bg-amber-700"
                disabled={submitting}
                data-testid="retomar-btn-ir"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando…
                  </>
                ) : (
                  <>
                    Retomar inscripción
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
            <div className="text-center mt-4">
              <Link
                to="/auxiliares-justicia/otros-distritos"
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                ← Empezar una nueva inscripción
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
